import {AfterViewInit, Component, DestroyRef, ElementRef, inject, model, OnInit, ChangeDetectionStrategy} from "@angular/core"
import {MatSelectModule} from "@angular/material/select"
import {UtilsService} from "@legacy/helpers/utils"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {BehaviorSubject, filter, firstValueFrom, fromEvent} from "rxjs"
import {NodesService} from "@node-editor/services/nodes.service"
import {DragService} from "@node-editor/services/drag.service"
import {SocketConnectionsService} from "@app/socket-editor/services/socket-connections.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-node-base",
    templateUrl: "./node-base.component.html",
    styleUrls: ["./node-base.component.scss"],
    imports: [MatSelectModule],
})
export class NodeBaseComponent<NodeType extends {id: string}> implements AfterViewInit, OnInit {
    id: string | undefined | null = null

    readonly $selected = model(false, {alias: "selected"})

    readonly connections = inject(SocketConnectionsService)
    readonly destroyRef = inject(DestroyRef)
    readonly drag = inject(DragService)
    readonly element = inject(ElementRef)
    readonly nodes = inject(NodesService)
    readonly utils = inject(UtilsService)

    ngOnInit() {
        fromEvent<MouseEvent>(this.element.nativeElement, "mousedown")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: MouseEvent) => this.onNodeMouseDown(event))
        fromEvent<MouseEvent>(this.element.nativeElement, "mouseup")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: MouseEvent) => this.onNodeMouseUp(event))
        fromEvent<MouseEvent>(this.element.nativeElement, "click")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: MouseEvent) => this.onClick(event))
    }

    private onNodeMouseDown(event: MouseEvent): void {
        if (event.button === 0) {
            this.drag.startDraggingNode(event, this)
            this.nodes.setSelected([this.node.id])
            event.stopPropagation()
        }
    }

    private onNodeMouseUp(event: MouseEvent): void {
        this.drag.stopDraggingNode(event)
    }

    private onClick(event: MouseEvent): void {
        // don't propagate to editor to prevent deselection
        event.stopPropagation()
    }

    public afterInitCompleted$ = new BehaviorSubject<boolean>(false)
    public waitForInit = firstValueFrom(this.afterInitCompleted$.pipe(filter((value) => value)))

    ngAfterViewInit(): void {
        this.afterInitCompleted$.next(true)
        this.nodes.onSelect.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newlySelected) => {
            this.$selected.update((selected) => !selected && newlySelected.some((selectedNode) => selectedNode.id === this.node.id))
        })
        this.nodes.onDeselect.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((deselected) => {
            this.$selected.update((selected) => selected && !deselected.some((deselectedNode) => deselectedNode.id === this.node.id))
        })
    }

    private _node!: NodeType
    set node(n: NodeType) {
        this._node = n
    }

    get node() {
        return this._node
    }

    private _position = {x: 0, y: 0}
    set position({x, y}: {x: number; y: number}) {
        this._position.x = x
        this._position.y = y
        this.element.nativeElement.setAttribute("style", `transform: translate(${x}px, ${y}px)`)
    }
    get position() {
        return this._position
    }
}
